import { Dropdown, Label } from "@heroui/react";
import { UserRoleEnum } from "@open-booking/shared";
import { LogOut } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { AUTH_ROUTES } from "@/src/auth/constants/auth-routes";
import { useLogout } from "@/src/auth/hooks/use-logout";
import { getIsAuthenticated, getUser } from "@/src/auth/store/auth-selectors";
import logo from "@/src/common/assets/images/logo.png";
import { RouteFallback } from "@/src/common/components/route-fallback";
import { env } from "@/src/common/constants/env";
import { useAppSelector } from "@/src/common/redux/store";
import { getInitials } from "@/src/common/utils/get-initials";
import { TENANT_ROUTES } from "@/src/tenant/constants/tenant-routes";

export function TenantRouteLayout() {
	const { t } = useTranslation("tenant");
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const user = useAppSelector(getUser);
	const location = useLocation();
	const logout = useLogout();

	if (!isAuthenticated) {
		const returnTo = `${location.pathname}${location.search}`;
		const authUrl = `${AUTH_ROUTES.AUTH}?redirect=${encodeURIComponent(returnTo)}`;
		return <Navigate to={authUrl} replace />;
	}

	if (user?.role !== UserRoleEnum.TENANT) {
		return <Navigate to="/" replace />;
	}

	const navLinks = [
		{ label: t("nav.bookings"), path: TENANT_ROUTES.BOOKINGS },
		{ label: t("nav.cautions"), path: TENANT_ROUTES.CAUTIONS },
		{ label: t("nav.profile"), path: TENANT_ROUTES.PROFILE },
	];

	return (
		<div className="flex min-h-screen flex-col bg-background">
			<nav className="flex items-center justify-between border-b border-default-200 bg-surface px-4 md:px-12 py-4">
				<Link to={TENANT_ROUTES.BOOKINGS} className="flex items-center gap-2.5">
					<img src={logo} alt={env.APP_NAME} className="size-6" />
					<span className="text-base font-bold text-foreground hidden md:block">{env.APP_NAME}</span>
				</Link>

				<div className="flex items-center gap-5">
					{navLinks.map((link) => {
						const isActive = location.pathname.startsWith(link.path);
						return (
							<Link
								key={link.path}
								to={link.path}
								className={`text-[13px] font-semibold ${isActive ? "text-accent" : "text-default-500 hover:text-foreground"}`}
							>
								{link.label}
							</Link>
						);
					})}
					<Dropdown>
						<Dropdown.Trigger className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-default-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
							<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
								{getInitials(user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null, user.email)}
							</div>
							<span className="hidden text-[13px] font-semibold text-default-600 md:inline">
								{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
							</span>
						</Dropdown.Trigger>
						<Dropdown.Popover placement="bottom end" className="min-w-[220px]">
							<Dropdown.Menu onAction={(key) => key === "logout" && logout()}>
								<Dropdown.Item id="logout" textValue={t("nav.logout")} variant="danger">
									<div className="flex items-center gap-2">
										<LogOut className="size-4" />
										<Label>{t("nav.logout")}</Label>
									</div>
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown.Popover>
					</Dropdown>
				</div>
			</nav>
			<Suspense fallback={<RouteFallback />}>
				<Outlet />
			</Suspense>
		</div>
	);
}
